import {
  INTRANET_CONTENT_KIND_LABELS,
  INTRANET_SEARCH_MIN_QUERY,
  IntranetSearchHitType,
  type IntranetSearchHit,
} from '@gb-ticket/shared-types';
import { SearchIcon, XIcon } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router';

import { cn } from '@/lib/utils';
import { INTRANET_HERO_ICON_COLOR } from '@/products/intranet/components/intranet-hero-icon-button';
import { useIntranetSearch } from '@/products/intranet/features/intranet/use-intranet-directory';

const SEARCH_DEBOUNCE_MS = 300;
const POPOVER_LIMIT = 8;

export function useIntranetFeedSearchState(initialQuery = '') {
  const listId = useId();
  const [draft, setDraft] = useState(initialQuery);
  const [q, setQ] = useState(initialQuery.trim());
  const [expanded, setExpanded] = useState(initialQuery.trim().length >= INTRANET_SEARCH_MIN_QUERY);
  const [open, setOpen] = useState(initialQuery.trim().length >= INTRANET_SEARCH_MIN_QUERY);
  const enabled = q.length >= INTRANET_SEARCH_MIN_QUERY;
  const search = useIntranetSearch({ q }, enabled && open);
  const matches = search.data?.items ?? [];
  const hits = expanded ? matches : matches.slice(0, POPOVER_LIMIT);

  useEffect(() => {
    const handle = window.setTimeout(() => setQ(draft.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [draft]);

  function showAll() {
    const term = draft.trim();
    if (term.length < INTRANET_SEARCH_MIN_QUERY) {
      return;
    }
    setQ(term);
    setExpanded(true);
    setOpen(true);
  }

  return {
    listId,
    draft,
    setDraft,
    enabled,
    open,
    setOpen,
    expanded,
    setExpanded,
    showAll,
    search,
    hits,
  };
}

type SearchResultsProps = {
  listId: string;
  enabled: boolean;
  open: boolean;
  expanded: boolean;
  search: ReturnType<typeof useIntranetSearch>;
  hits: IntranetSearchHit[];
  onPick: () => void;
  className?: string;
};

function IntranetFeedSearchResults({
  listId,
  enabled,
  open,
  expanded,
  search,
  hits,
  onPick,
  className,
}: SearchResultsProps) {
  if (!open || !enabled) {
    return null;
  }

  return (
    <div id={listId} className={className}>
      {search.isPending ? (
        <p className="px-3 py-2 text-sm text-muted-foreground">Suche läuft …</p>
      ) : search.isError ? (
        <p role="alert" className="px-3 py-2 text-sm text-destructive">
          Die Suche ist fehlgeschlagen.
        </p>
      ) : hits.length === 0 ? (
        <p className="px-3 py-2 text-sm text-muted-foreground">Keine Treffer.</p>
      ) : (
        <ul>
          {hits.map((hit) => (
            <li key={`${hit.type}-${hit.id}`}>
              <SearchHit hit={hit} onPick={onPick} />
            </li>
          ))}
        </ul>
      )}
      {expanded ? null : (
        <button
          type="submit"
          className="flex min-h-11 w-full items-center border-t px-3 text-left text-sm font-medium text-primary hover:bg-muted"
        >
          Alle Ergebnisse
        </button>
      )}
    </div>
  );
}

/** Inline field for the desktop feed toolbar. */
export function IntranetFeedSearchToolbar({ initialQuery = '' }: { initialQuery?: string }) {
  const state = useIntranetFeedSearchState(initialQuery);

  return (
    <form
      className="relative min-w-40 flex-1"
      onSubmit={(event) => {
        event.preventDefault();
        state.showAll();
      }}
    >
      <label className="sr-only" htmlFor="intranet-feed-search">
        Intranet durchsuchen
      </label>
      <SearchIcon
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <input
        id="intranet-feed-search"
        value={state.draft}
        placeholder="Suchen …"
        autoComplete="off"
        aria-expanded={state.open && state.enabled}
        aria-controls={state.listId}
        className="h-11 w-full rounded-lg border bg-background pr-3 pl-9 text-sm text-foreground placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        onChange={(event) => {
          state.setDraft(event.target.value);
          state.setExpanded(false);
          state.setOpen(true);
        }}
        onFocus={() => state.setOpen(true)}
      />
      <IntranetFeedSearchResults
        {...state}
        onPick={() => state.setOpen(false)}
        className="absolute z-20 mt-1 max-h-96 w-full overflow-y-auto rounded-lg border bg-popover shadow-md"
      />
    </form>
  );
}

const SEARCH_EXPAND_MS = 320;

/** Hero search on mobile: the icon grows into a short field. */
export function IntranetHeroExpandingSearch() {
  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const state = useIntranetFeedSearchState('');

  function setSearchExpanded(next: boolean) {
    setExpanded(next);
    if (!next) {
      state.setDraft('');
      state.setOpen(false);
      state.setExpanded(false);
    }
  }

  useEffect(() => {
    if (!expanded) {
      return;
    }
    const handle = window.setTimeout(() => inputRef.current?.focus(), SEARCH_EXPAND_MS);
    return () => window.clearTimeout(handle);
  }, [expanded]);

  return (
    <form
      className={cn(
        'relative overflow-hidden rounded-full border shadow-sm backdrop-blur-md',
        'transition-[width,background-color,border-color,box-shadow] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
        expanded
          ? 'w-full max-w-[13.5rem] border-border bg-card text-card-foreground'
          : 'w-11 border-white/35 bg-brand-navy/40 hover:bg-brand-navy/55',
      )}
      onSubmit={(event) => {
        event.preventDefault();
        if (!expanded) {
          setSearchExpanded(true);
          return;
        }
        state.showAll();
      }}
    >
      <div className="flex h-11 items-center">
        <button
          type="button"
          aria-label="Intranet durchsuchen"
          aria-expanded={expanded}
          className={cn(
            'inline-flex size-11 shrink-0 items-center justify-center focus-visible:ring-2 focus-visible:outline-none',
            expanded
              ? 'text-primary focus-visible:ring-ring'
              : 'text-white focus-visible:ring-white',
          )}
          onClick={() => {
            if (!expanded) {
              setSearchExpanded(true);
            }
          }}
        >
          <SearchIcon
            aria-hidden
            size={22}
            {...(expanded ? {} : { color: INTRANET_HERO_ICON_COLOR })}
            strokeWidth={2.25}
            className={expanded ? 'text-primary' : 'text-white'}
          />
        </button>
        <label className="sr-only" htmlFor="intranet-feed-search-mobile">
          Intranet durchsuchen
        </label>
        <input
          ref={inputRef}
          id="intranet-feed-search-mobile"
          value={state.draft}
          placeholder="Suchen …"
          autoComplete="off"
          tabIndex={expanded ? 0 : -1}
          aria-expanded={state.open && state.enabled}
          aria-controls={state.listId}
          className={cn(
            'min-w-0 flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none',
            'transition-opacity duration-200 ease-out motion-reduce:transition-none',
            expanded ? 'opacity-100' : 'pointer-events-none w-0 opacity-0',
          )}
          onChange={(event) => {
            state.setDraft(event.target.value);
            state.setExpanded(false);
            state.setOpen(true);
          }}
        />
        <button
          type="button"
          aria-label="Suche schließen"
          tabIndex={expanded ? 0 : -1}
          className={cn(
            'mr-1 inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground',
            'transition-opacity duration-200 ease-out motion-reduce:transition-none',
            'hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
            expanded ? 'opacity-100' : 'pointer-events-none w-0 opacity-0',
          )}
          onClick={() => setSearchExpanded(false)}
        >
          <XIcon className="size-4" aria-hidden />
        </button>
      </div>
      <IntranetFeedSearchResults
        {...state}
        onPick={() => setSearchExpanded(false)}
        className="absolute z-30 mt-2 max-h-[min(50dvh,20rem)] w-full overflow-y-auto rounded-xl border bg-popover shadow-lg"
      />
    </form>
  );
}

function SearchHit({ hit, onPick }: { hit: IntranetSearchHit; onPick: () => void }) {
  return (
    <Link
      to={hit.href}
      onClick={onPick}
      className="flex min-h-11 items-center justify-between gap-2 px-3 text-sm hover:bg-muted"
    >
      <span className="truncate">{hit.title}</span>
      <span className="shrink-0 text-xs text-muted-foreground">{hitKindLabel(hit)}</span>
    </Link>
  );
}

function hitKindLabel(hit: IntranetSearchHit): string {
  if (hit.type === IntranetSearchHitType.COLLEAGUE) {
    return 'Kollege';
  }
  return hit.kind ? INTRANET_CONTENT_KIND_LABELS[hit.kind] : 'Beitrag';
}
